// eslint-disable-next-line no-restricted-imports
import { BigNumber } from '@ethersproject/bignumber'
import { TransactionResponse } from '@ethersproject/providers'
import { Trans } from '@lingui/macro'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useStakeCusdContract } from 'hooks/useContract'
import { ReactNode, useMemo } from 'react'
import { calculateGasMargin } from 'utils/calculateGasMargin'

export enum StakeCallbackState {
  INVALID,
  LOADING,
  VALID,
}

interface UseBorrowCallbackReturns {
  state: StakeCallbackState
  callback?: () => Promise<TransactionResponse>
  error?: ReactNode
}

interface UseStakeCallbackArgs {
  stakeAmount: BigNumber | undefined
}

// returns a function that will execute a swap, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function useStake({ stakeAmount }: UseStakeCallbackArgs): UseBorrowCallbackReturns {
  const { account, chainId, library } = useActiveWeb3React()

  const stake = useStakeCusdContract()

  return useMemo(() => {
    if (!stakeAmount || !library || !account || !chainId || !stake) {
      return { state: StakeCallbackState.INVALID, error: <Trans>Missing dependencies</Trans> }
    }

    return {
      state: StakeCallbackState.VALID,
      callback: async () => {
        const estimatedGas = await stake.estimateGas.deposit(stakeAmount, account).catch(() => {
          // general fallback for tokens which restrict approval amounts
          throw Error('Invalid borrow amount')
        })

        return stake
          .deposit(stakeAmount, account, {
            gasLimit: calculateGasMargin(estimatedGas),
          })
          .catch((error: Error) => {
            throw error
          })
      },
    }
  }, [library, account, chainId, stakeAmount, stake])
}
